import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowUpRight,
  ChevronRight,
  FileText,
  HeartPulse,
  ListChecks,
  PlayCircle,
  ScrollText,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Media } from "@/components/Media";
import { toast } from "@/components/ui/sonner";
import { trackDemoClick } from "@/lib/telemetry";
import { MOBILE_TABS, MobileBottomNav, type MobileTabId } from "./MobileTabs";
import { promptInstall } from "./install";
import { MobileMenu } from "./MobileMenu";
import { RegulatoryNotice } from "@/components/ds";
import { HomeHero } from "@/components/home/HomeHero";
import { PersonalizationPrompt } from "@/components/personalization/PersonalizationPrompt";
import { PersonalizedGuide } from "@/components/personalization/PersonalizedGuide";
import { CategoryLeadership } from "@/components/category/CategoryLeadership";
import { KeyBenefits } from "@/components/KeyBenefits";
import { HowItWorks } from "@/components/HowItWorks";
import { VideoSection } from "@/components/VideoSection";
import logoAsset from "@/assets/datasyteAI-logo-black.png.asset.json";

const MomentsSequence = lazy(() => import("@/components/home/MomentsSequence").then((m) => ({ default: m.MomentsSequence })));
const PhotoMosaic = lazy(() => import("@/components/PhotoMosaic").then((m) => ({ default: m.PhotoMosaic })));
const TrainSection = lazy(() => import("@/components/TrainSection").then((m) => ({ default: m.TrainSection })));
const BenefitsGallery = lazy(() => import("@/components/BenefitsGallery").then((m) => ({ default: m.BenefitsGallery })));
const ProblemSection = lazy(() => import("@/components/ProblemSection").then((m) => ({ default: m.ProblemSection })));
const SolutionSection = lazy(() => import("@/components/SolutionSection").then((m) => ({ default: m.SolutionSection })));
const TechnologySection = lazy(() => import("@/components/TechnologySection").then((m) => ({ default: m.TechnologySection })));
const CompetitionSection = lazy(() => import("@/components/CompetitionSection").then((m) => ({ default: m.CompetitionSection })));
const MarketSection = lazy(() => import("@/components/MarketSection").then((m) => ({ default: m.MarketSection })));
const BusinessSection = lazy(() => import("@/components/BusinessSection").then((m) => ({ default: m.BusinessSection })));
const SecuritySection = lazy(() => import("@/components/SecuritySection").then((m) => ({ default: m.SecuritySection })));
const ComplianceSection = lazy(() => import("@/components/ComplianceSection").then((m) => ({ default: m.ComplianceSection })));
const ContactSection = lazy(() => import("@/components/ContactSection").then((m) => ({ default: m.ContactSection })));
const InvestButton = lazy(() => import("@/components/InvestButton").then((m) => ({ default: m.InvestButton })));
const FlipBookSection = lazy(() => import("@/components/FlipBookSection").then((m) => ({ default: m.FlipBookSection })));
const Footer = lazy(() => import("@/components/Footer").then((m) => ({ default: m.Footer })));

const Fallback = () => <div className="min-h-[30vh]" />;

/**
 * Quick tile. Icon treatment intentionally matches the desktop CapabilityCard
 * (size-10 bordered square, primary lucide glyph) so the mobile app shell uses
 * the same iconography as the desktop site.
 */
const QuickTile = ({
  label,
  hint,
  icon: Icon,
  onClick,
}: {
  label: string;
  hint: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex h-full min-h-[132px] min-w-0 flex-col items-start justify-start gap-2 rounded-2xl border border-border bg-card p-4 text-left shadow-sm transition-transform active:scale-[0.98]"
  >
    <span className="inline-flex size-10 items-center justify-center rounded-lg border border-border bg-surface text-primary">
      <Icon className="size-5" aria-hidden="true" />
    </span>
    <span className="w-full min-w-0 break-words text-[15px] font-bold leading-snug text-foreground">{label}</span>
    <span className="w-full min-w-0 break-words text-xs leading-snug text-muted-foreground">{hint}</span>
  </button>
);

/** Row-style link tile with the same icon language as the quick tiles. */
const MobileLinkTile = ({
  label,
  icon: Icon,
  to,
  href,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to?: string;
  href?: string;
}) => {
  const inner = (
    <>
      <span className="flex items-center gap-3">
        <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-primary">
          <Icon className="size-5" aria-hidden="true" />
        </span>
        <span className="font-semibold text-foreground">{label}</span>
      </span>
      {to ? (
        <ChevronRight className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
      ) : (
        <ArrowUpRight className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
      )}
    </>
  );

  const className =
    "flex min-h-16 items-center justify-between gap-3 rounded-2xl border border-border bg-card px-4 py-3 shadow-sm transition-transform active:scale-[0.98]";

  if (to) {
    return (
      <Link to={to} className={className}>
        {inner}
      </Link>
    );
  }
  return (
    <a href={href} className={className}>
      {inner}
    </a>
  );
};

export const MobileApp = ({ initialTab = "home" }: { initialTab?: MobileTabId }) => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") as MobileTabId | null;
  const [tab, setTab] = useState<MobileTabId>(
    tabParam && MOBILE_TABS.some((t) => t.id === tabParam) ? tabParam : initialTab,
  );

  useEffect(() => {
    if (tabParam && MOBILE_TABS.some((t) => t.id === tabParam)) setTab(tabParam);
  }, [tabParam]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const meta = MOBILE_TABS.find((t) => t.id === tab)!;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "auto" });
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [tab]);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      (window as any).deferredInstallPrompt = e;
    };
    const onInstalled = () => {
      (window as any).deferredInstallPrompt = null;
      toast.success("AiMediQ™ installed!");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur-md">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <Media
            src={logoAsset.url}
            alt="datasyteAI™ by PathFree Technologies logo"
            className="h-9 w-auto object-contain"
            sizes="150px"
            eager
          />
          <div className="flex shrink-0 items-center gap-2">
            {tab !== "home" && (
              <Button asChild size="sm" className="min-h-11 rounded-full px-4">
                <a
                  href="https://pathfree.com/contact"
                  onClick={() => trackDemoClick("mobile_app_header")}
                >
                  Demo
                </a>
              </Button>
            )}
            <MobileMenu />
          </div>
        </div>
        <div className="px-4 pb-3">
          {tab === "home" ? (
            <p className="text-xl font-extrabold leading-tight text-foreground">{meta.title}</p>
          ) : (
            <h1 className="text-xl font-extrabold leading-tight text-foreground">{meta.title}</h1>
          )}
          <p className="text-sm text-muted-foreground">{meta.subtitle}</p>
        </div>
      </header>

      <main ref={scrollRef} className="pb-28">
        {tab === "home" && (
          <>
            <HomeHero />
            <PersonalizationPrompt />
            <Suspense fallback={<Fallback />}>
              <PersonalizedGuide />
            </Suspense>
            <div className="px-4">
              <CategoryLeadership bare />
            </div>
            <section className="px-4 py-8" aria-label="Quick links">
              <h2 className="mb-4 text-lg font-bold text-foreground">What do you want to see?</h2>
              <div className="grid auto-rows-fr grid-cols-2 gap-3">
                <QuickTile icon={PlayCircle} label="What is a Code Blue?" hint="Watch a short video" onClick={() => setTab("learn")} />
                <QuickTile icon={ListChecks} label="How it works" hint="3 easy steps" onClick={() => setTab("learn")} />
                <QuickTile icon={HeartPulse} label="The smart cart" hint="See AiMediQ™" onClick={() => setTab("product")} />
                <QuickTile icon={TrendingUp} label="Invest with us" hint="Market & model" onClick={() => setTab("invest")} />
              </div>
            </section>
            <Suspense fallback={<Fallback />}>
              <MomentsSequence />
            </Suspense>
            <KeyBenefits />
          </>
        )}

        {tab === "learn" && (
          <Suspense fallback={<Fallback />}>
            <section className="px-4 pt-4" aria-label="Regulatory status">
              <RegulatoryNotice title="Pre-market development and validation">
                AiMediQ™ powered by datasyteAI™ is not FDA cleared or approved, is not available for sale,
                and is not for clinical use. Capabilities shown include work in development or planned;
                visuals are conceptual and use synthetic data. Nothing here is medical advice, an offer to
                sell securities, or a claim of improved patient outcomes.
              </RegulatoryNotice>
            </section>
            <VideoSection />
            <HowItWorks />
            <ProblemSection />
            <PhotoMosaic />
          </Suspense>
        )}

        {tab === "product" && (
          <Suspense fallback={<Fallback />}>
            <section className="px-4 pt-4" aria-label="Regulatory status">
              <RegulatoryNotice title="Pre-market development and validation">
                AiMediQ™ powered by datasyteAI™ is not FDA cleared or approved, is not available for sale,
                and is not for clinical use. Capabilities shown include work in development or planned;
                visuals are conceptual and use synthetic data. Nothing here is medical advice, an offer to
                sell securities, or a claim of improved patient outcomes.
              </RegulatoryNotice>
            </section>
            <SolutionSection />
            <TechnologySection />
            <TrainSection />
            <BenefitsGallery />
            <SecuritySection />
            <ComplianceSection />
          </Suspense>
        )}

        {tab === "invest" && (
          <Suspense fallback={<Fallback />}>
            <section className="px-4 pt-4" aria-label="Regulatory status">
              <RegulatoryNotice title="Pre-market development and validation">
                AiMediQ™ powered by datasyteAI™ is not FDA cleared or approved, is not available for sale,
                and is not for clinical use. Capabilities shown include work in development or planned;
                visuals are conceptual and use synthetic data. Nothing here is medical advice, an offer to
                sell securities, or a claim of improved patient outcomes.
              </RegulatoryNotice>
            </section>
            <MarketSection />
            <BusinessSection />
            <CompetitionSection />
            <InvestButton />
            <section className="px-4 pb-4" aria-label="More investor information">
              <MobileLinkTile
                icon={TrendingUp}
                label="More information"
                href="https://pathfree.com/investors"
              />
            </section>
            <FlipBookSection />
          </Suspense>
        )}

        {tab === "contact" && (
          <Suspense fallback={<Fallback />}>
            <ContactSection />
            <section className="px-4 pb-8" aria-label="Legal">
              <h2 className="mb-3 text-lg font-bold text-foreground">The fine print</h2>
              <div className="grid gap-3">
                <MobileLinkTile icon={ScrollText} label="Disclaimers" to="/disclaimers" />
                <MobileLinkTile icon={ShieldCheck} label="Privacy Policy" to="/privacy" />
                <MobileLinkTile icon={FileText} label="Terms of Service" to="/terms" />
              </div>
            </section>
            <Footer />
          </Suspense>
        )}
      </main>

      <MobileBottomNav active={tab} onChange={setTab} onInstall={promptInstall} />
    </div>
  );
};
